import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import Checkbox from "@cs/components/src/Checkbox";
import Input from "@cs/components/src/Input";
import ListInput from "@cs/components/src/ListInput";
import Select, { Option } from "@cs/components/src/Select";
import OptionInput from "@cs/components/src/OptionInput";
import ApiService from "../../services/ApiService";
import Button from "@cs/components/src/Button";
import Tooltip from "@cs/components/src/Tooltip";
import { ExternalLink } from "react-feather";
import Description from "@cs/components/src/Description";
import { isVariableSeparator } from "./utils";
import StackedStringList from "./StackedStringList";
import RepeatableGroup from "./RepeatableGroup";

type Props = {
  variable: VariableConfig;
  variables: VariableInstance[];
  value: any;
  onChange: (value: any) => void;
  systemDomainId?: string;
  label?: string;
  config?: PluginConfigItem | ScriptConfigItem | ListInputConfig;
  /** When true, render collapsed/summary view (read-only tags/values).
   *  When false (default), render expanded/editable view. */
  isCollapsed?: boolean;
  /** H1: server-resolved friendly labels keyed by variableConfigId. */
  variableLabels?: Record<string, string>;
};

// ---------------------------------------------------------------------------
// Styled components — MDV-matching styles
// ---------------------------------------------------------------------------

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  color: var(--gray200);
  font-size: 14px;
`;

const StyledDescription = styled(Description)`
  margin-top: -20px;
  margin-bottom: 20px;
`;

// Two-column field row: label (1fr) + value cell (2.5fr) — matches MDV compact row.
const FieldRow = styled.div<{ $alignTop?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 2.5fr;
  column-gap: 16px;
  align-items: ${(p) => (p.$alignTop ? "flex-start" : "center")};
  padding: 3px 10px;
`;

const FieldLabel = styled.span`
  color: var(--gray300);
  font-size: 14px;
  text-align: right;
  align-self: center;
  word-break: break-word;
`;

// Dotted underline on the label text when a tooltip description is present.
const LabelText = styled.span<{ $hasTooltip?: boolean; $hasLink?: boolean }>`
  text-decoration-line: ${(p) => (p.$hasTooltip ? "underline" : "none")};
  text-decoration-style: dotted;
  text-decoration-color: var(--dark700);
  cursor: ${(p) =>
    p.$hasLink ? (p.$hasTooltip ? "help" : "default") : "unset"};
`;

const LabelLink = styled.a`
  color: var(--blue300);
  display: inline-flex;
  align-items: center;
  gap: 5px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    > span {
      text-decoration-style: solid;
      text-decoration-color: var(--blue300);
    }
  }
  > svg {
    flex-shrink: 0;
  }
`;

const FieldValue = styled.div`
  min-width: 0;
`;

// Read-only value display used when collapsed (summary view).
const ReadValue = styled.span`
  color: var(--gray400);
  font-size: 14px;
  word-break: break-word;
  display: block;
  padding: 4px 0;
  min-height: 30px;
`;

// MDV-style Tag for multi-value read view (collapsed string_list).
const Tag = styled.span`
  background-color: var(--dark400);
  padding: 2px 5px;
  color: var(--gray500);
  border-radius: 3px;
  font-size: 14px;
  word-break: break-word;
`;

const TagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 5px 0;
  column-gap: 8px;
  row-gap: 3px;
  min-height: 30px;
`;

// MDV input-style wrapper: sets --inputBackground to a non-transparent value
// so the dark input background is visible. MDV sets this in its edit Wrapper.
const EditWrapper = styled.div<{ $twoColumns?: boolean }>`
  --inputBorder: var(--dark300);
  --inputBackground: var(--dark200);

  ${(props) =>
    props.$twoColumns &&
    css`
      display: grid;
      grid-template-columns: 1fr auto;
      column-gap: 10px;
    `}

  input:not(.react-select__input),
  textarea {
    height: 30px;
    font-size: 15px;
  }

  .react-select__control {
    min-height: 30px;
    font-size: 15px;
    flex-grow: 1;
  }
`;

// MDV-matching size props — applied to all Input and Select instances.
const SELECT_STYLES = { minHeight: 30, fontSize: 15 };
const INPUT_SIZE = { buttonSize: 26, minWidth: 50 } as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Renders the label cell. When `link` is provided, the entire label (text + icon)
// becomes a blue anchor. Tooltip still wraps when a description is present.
const Label = ({
  children,
  description,
  link,
}: {
  children: React.ReactNode;
  description?: string;
  link?: string;
}) => {
  const inner = link ? (
    <LabelLink
      href={link}
      target="_blank"
      rel="noreferrer"
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      <ExternalLink size={15} />
    </LabelLink>
  ) : (
    <LabelText>{children}</LabelText>
  );

  return (
    <FieldLabel>
      {description ? (
        <Tooltip label={description} placement="top">
          {link ? (
            <LabelLink
              href={link}
              target="_blank"
              rel="noreferrer"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
            >
              <LabelText $hasTooltip>{children}</LabelText>
              <ExternalLink size={15} />
            </LabelLink>
          ) : (
            <LabelText $hasTooltip>{children}</LabelText>
          )}
        </Tooltip>
      ) : (
        inner
      )}
    </FieldLabel>
  );
};

// ---------------------------------------------------------------------------
// VariableInput
// ---------------------------------------------------------------------------

const VariableInput = (props: Props) => {
  const {
    variable,
    variables,
    value,
    onChange,
    systemDomainId,
    label,
    config,
    isCollapsed = false,
    variableLabels,
  } = props;

  // The config key for this variable — used to look up server-resolved labels (H1).
  const variableConfigId = variable.id ?? variable.key;

  // serverLabel is resolved at load time for the initial value. Track that
  // value so we stop using the stale label once the user picks something else.
  const serverLabelValueRef = useRef<unknown>(value);
  const prevVariableConfigIdRef = useRef<string | undefined>(variableConfigId);
  if (prevVariableConfigIdRef.current !== variableConfigId) {
    prevVariableConfigIdRef.current = variableConfigId;
    serverLabelValueRef.current = value;
  }

  const {
    dataType,
    required: requiredConfig,
    description: descriptionConfig,
    title: titleConfig,
  } = variable;
  const [currentSystemDomainId, setCurrentSystemDomainId] = useState<
    string | undefined
  >();
  const [disabled, setDisabled] = useState(false);

  const title = label ? label : titleConfig;
  const required = label ? false : requiredConfig;
  const description = label ? undefined : descriptionConfig;
  const link = label ? undefined : variable.link;

  // True when showing collapsed/summary view — render read-only.
  // False (default) = expanded = editable.
  const isReadView = isCollapsed;

  let safeValue = value;
  if (
    dataType.endsWith("_list") &&
    value !== undefined &&
    value !== null &&
    !Array.isArray(value)
  ) {
    safeValue = [];
  }

  useEffect(() => {
    setDisabled(false);
    variable.linkedVariables?.forEach(({ type, variableConfigId }) => {
      const value = variables.find(
        ({ variableConfigId: id }) => id === variableConfigId
      )?.value;
      if (value) {
        if (type === "system_domain_id") {
          const newSystemDomainId = value.toString();
          if (newSystemDomainId !== currentSystemDomainId) {
            setCurrentSystemDomainId(newSystemDomainId);

            if (currentSystemDomainId) {
              onChange(null);
            }
          }
        }
      } else {
        setDisabled(true);
      }
    });
  }, [variable.linkedVariables, variables, currentSystemDomainId]);

  const resolvedSystemDomainId = currentSystemDomainId || systemDomainId;

  const optionsAsync = (systemDomainId: string, dataType: string) => ({
    key: `${systemDomainId}/${dataType}`,
    fn: async () =>
      (
        await ApiService.fetch<{ data: Option[] }>(
          "GET",
          `/api/select_option/${systemDomainId}/${dataType}`
        )
      ).data,
  });

  const getInput = () => {
    if (isVariableSeparator(dataType)) {
      // Separator: consumed by VariableInputForm — safety fallback.
      return null;
    } else if (dataType === "boolean") {
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            <Checkbox
              value={safeValue}
              onChange={isReadView ? () => {} : onChange}
              disabled={isReadView}
            />
          </FieldValue>
        </FieldRow>
      );
    } else if (
      dataType === "number" ||
      dataType === "string" ||
      dataType === "string_multiline"
    ) {
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{safeValue ?? ""}</ReadValue>
            ) : (
              <EditWrapper>
                <Input
                  required={required}
                  value={safeValue ?? ""}
                  onChange={onChange}
                  numeric={dataType === "number"}
                  textarea={dataType === "string_multiline"}
                  height={dataType === "string_multiline" ? 180 : 30}
                  {...INPUT_SIZE}
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType === "date") {
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{safeValue ?? ""}</ReadValue>
            ) : (
              <EditWrapper>
                <Input
                  type="date"
                  required={required}
                  value={safeValue ?? ""}
                  onChange={onChange}
                  {...INPUT_SIZE}
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType === "string_list") {
      // #10: collapsed = tags (no remove); expanded = stacked editable inputs with X.
      return (
        <FieldRow $alignTop>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              // Collapsed/summary: render MDV-style tags, not removable.
              <TagWrapper>
                {(safeValue ?? []).length === 0 ? (
                  <ReadValue />
                ) : (
                  (safeValue ?? []).map((item: string, i: number) => (
                    <Tag key={i}>{item}</Tag>
                  ))
                )}
              </TagWrapper>
            ) : (
              // Expanded/editable: stacked inputs with remove X on each.
              <EditWrapper>
                <StackedStringList
                  value={safeValue ?? []}
                  onChange={onChange}
                  required={required}
                  isExpanded
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType === "dropdown") {
      const optionLabel =
        variable.options?.find((o) => o.value === safeValue)?.label ??
        safeValue ??
        "";
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{optionLabel}</ReadValue>
            ) : (
              <EditWrapper>
                <Select
                  options={variable.options}
                  value={safeValue}
                  onChange={({ value }) => onChange(value)}
                  onClear={() => onChange(null)}
                  required={required}
                  portalTarget={document.getElementById("PortalTarget")}
                  containerStyles={SELECT_STYLES}
                  buttonSize={26}
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType === "dropdown_list") {
      const optionLabels = (safeValue ?? [])
        .map(
          (v: string) =>
            variable.options?.find((o) => o.value === v)?.label ?? v
        )
        .join(", ");
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{optionLabels}</ReadValue>
            ) : (
              <EditWrapper>
                <Select
                  options={variable.options}
                  value={safeValue ?? []}
                  onChange={(options) =>
                    onChange(options.map(({ value }) => value))
                  }
                  required={required}
                  isMulti
                  portalTarget={document.getElementById("PortalTarget")}
                  containerStyles={SELECT_STYLES}
                  buttonSize={26}
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType === "dropdown_option_list") {
      // Rendered as a list — full width; keep ListInput.
      return (
        <EditWrapper>
          <_DropdownOptionListWidget
            title={title}
            safeValue={safeValue}
            onChange={onChange}
            required={required}
            description={description}
          />
        </EditWrapper>
      );
    } else if (dataType.startsWith("list_input_")) {
      // E1: ONE non-collapsible InputGroup titled with the mapping group title,
      // containing a list of collapsible sub-entries. RepeatableGroup owns the structure.
      const listInput = (
        "components" in (config ?? {})
          ? (config as PluginConfigItem).components.find(
              ({ type, id }) =>
                type === "list_input_config" &&
                id === dataType.replace("list_input_", "")
            )
          : undefined
      ) as ListInputConfig | undefined;
      if (listInput) {
        return (
          <RepeatableGroup
            listInput={listInput}
            groupTitle={title}
            value={safeValue ?? []}
            onChange={onChange}
            systemDomainId={resolvedSystemDomainId}
            config={config}
            required={required}
          />
        );
      } else {
        return (
          <>
            <Input
              label={title}
              value={"Missing list input"}
              onChange={() => {}}
              disabled
            />
            {description && <StyledDescription label={description} />}
          </>
        );
      }
    } else if (dataType.endsWith("_list") && resolvedSystemDomainId) {
      // Multi-value id-backed select (role_group_list, team_list, …).
      const countLabel = Array.isArray(safeValue)
        ? `${safeValue.length} selected`
        : "";
      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{countLabel}</ReadValue>
            ) : (
              <EditWrapper>
                <Select
                  optionsAsync={optionsAsync(
                    resolvedSystemDomainId,
                    dataType.replace("_list", "")
                  )}
                  value={safeValue ?? []}
                  onChange={(options) =>
                    onChange(options.map(({ value }) => value))
                  }
                  isMulti
                  disabled={disabled}
                  required={required}
                  portalTarget={document.getElementById("PortalTarget")}
                  containerStyles={SELECT_STYLES}
                  buttonSize={26}
                />
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    } else if (dataType && resolvedSystemDomainId) {
      // Single-value id-backed select.
      // In read/collapsed view: use H1 server-resolved label when available, else raw value.
      const serverLabel = variableConfigId
        ? variableLabels?.[variableConfigId]
        : undefined;
      const readLabel = serverLabel ?? safeValue ?? "";

      // #13: pass {label, value} to Select so the correct label shows immediately,
      // Seed display with serverLabel only while the value hasn't changed from
      // what the server resolved the label for. Once the user picks a different
      // value, fall back to a plain string so Select looks up the correct label
      // from its loaded options instead of showing the now-stale serverLabel.
      const selectValue: Option | string | undefined =
        safeValue !== undefined &&
        safeValue !== null &&
        serverLabel &&
        safeValue === serverLabelValueRef.current
          ? { label: serverLabel, value: safeValue }
          : safeValue;

      return (
        <FieldRow>
          <Label description={description} link={link}>
            {title}
          </Label>
          <FieldValue>
            {isReadView ? (
              <ReadValue>{readLabel}</ReadValue>
            ) : (
              <EditWrapper $twoColumns={dataType === "secret"}>
                <Select
                  optionsAsync={optionsAsync(resolvedSystemDomainId, dataType)}
                  value={selectValue}
                  onChange={({ value }) => onChange(value)}
                  onClear={() => onChange(null)}
                  disabled={disabled}
                  required={required}
                  portalTarget={document.getElementById("PortalTarget")}
                  containerStyles={SELECT_STYLES}
                  buttonSize={26}
                />
                {dataType === "secret" && (
                  <a
                    href={`/config/organization/${systemDomainId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button
                      label="Manage Secrets"
                      type="default"
                      icon={ExternalLink}
                    />
                  </a>
                )}
              </EditWrapper>
            )}
          </FieldValue>
        </FieldRow>
      );
    }

    return (
      <>
        <Input
          label={title}
          value={"Unknown data type " + dataType}
          onChange={() => {}}
          disabled
        />
        {description && <StyledDescription label={description} />}
      </>
    );
  };

  return <>{getInput()}</>;
};

// ---------------------------------------------------------------------------
// Internal sub-component for dropdown_option_list (unchanged from original).
// ---------------------------------------------------------------------------

const _DropdownOptionListWidget = ({
  title,
  safeValue,
  onChange,
  required,
  description,
}: {
  title?: string;
  safeValue: any;
  onChange: (v: any) => void;
  required: boolean;
  description?: string;
}) => (
  <ListInput
    label={title}
    value={safeValue ?? []}
    onChange={onChange}
    component={OptionInput}
    newValue={() => ({ label: "", value: "" })}
    required={required}
    description={description}
    extraProps={{ ...INPUT_SIZE, height: 30 }}
    labels={
      <TwoColumn>
        <span>Label:</span>
        <span>Value:</span>
      </TwoColumn>
    }
  />
);

export default VariableInput;
